import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Sparkles, BookOpen, CheckCircle2, ArrowRight, X, Loader2, Bookmark, Star, ChevronLeft
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { dbManager } from '../services/db_manager';
import { storageService } from '../services/storageService';
import { libraryService } from '../services/libraryService';
import { TARGET_LANG_NAMES } from '../constants/roadmapBonusData';

const LIBRARY_CATALOG_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json";
const R2_HTML_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/";

interface RecommendedBook {
  id: string;
  title: string;
  author: string;
  language: string;
  file: string;
  cover: string;
  description: string;
  reading_level: string | number;
  subjects: string[];
  downloads: number;
  mainGenre: string;
}

interface LevelCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  level: string; // e.g. 'A1', 'A2', 'B1', etc.
  targetLangCode: string; // e.g. 'en', 'fr', 'de', etc.
  nativeLangCode: string; // e.g. 'pt', 'en', etc.
  onContinueStudies: () => void;
}

export const LevelCompletionModal: React.FC<LevelCompletionModalProps> = ({
  isOpen,
  onClose,
  level,
  targetLangCode,
  nativeLangCode,
  onContinueStudies
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const cleanLang = (targetLangCode || 'en').split('-')[0].toLowerCase();
  const cleanNative = (nativeLangCode || 'pt').split('-')[0].toLowerCase();
  const levelUpper = (level || 'A1').toUpperCase();

  const [loading, setLoading] = useState(true);
  const [recommendedBooks, setRecommendedBooks] = useState<RecommendedBook[]>([]);
  const [selectedBook, setSelectedBook] = useState<RecommendedBook | null>(null);
  const [isBookSaved, setIsBookSaved] = useState(false);
  const [savingBookId, setSavingBookId] = useState<string | null>(null);

  const targetLangName = TARGET_LANG_NAMES[cleanLang]?.[cleanNative] || TARGET_LANG_NAMES[cleanLang]?.pt || 'Inglês';

  // Load and filter 5 books by popularity & 5 distinct genres
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    setLoading(true);
    setIsBookSaved(false);
    setSelectedBook(null);

    const loadRecommendations = async () => {
      try {
        const timestamp = Date.now();
        const res = await fetch(`${LIBRARY_CATALOG_URL}?v=${timestamp}`, { mode: 'cors' });
        if (!res.ok) throw new Error("Erro ao carregar catálogo de livros");
        const rawText = await res.text();
        const cleanText = rawText.replace(/: NaN/g, ': null');
        const data = JSON.parse(cleanText);

        if (!Array.isArray(data)) return;

        // 1. Normalize items
        const normalized = data.map((item: any) => {
          const id = String(item.id || item.gutenberg_id || item.ID || "").trim();
          if (!id || id === "undefined") return null;

          const rawLang = String(item.language || item.idioma || "und").trim();
          const itemLang = rawLang.split(/[;,\s]/)[0].toLowerCase();
          
          let cover = String(item.cover || item.capa || "").trim();
          if (cover.toLowerCase() === "vazio") cover = "";

          const fileUrl = `${R2_HTML_BASE}${id}.html`;
          const subjectsArr = Array.isArray(item.subject) ? item.subject : (item.subject ? [item.subject] : []);
          
          return {
            id,
            title: item.title || item.titulo || "Título Desconhecido",
            author: item.author || item.autor || "Autor Desconhecido",
            language: itemLang,
            file: fileUrl,
            cover,
            description: item.description || item.descricao || "",
            reading_level: item.reading_level,
            subjects: subjectsArr,
            downloads: parseInt(item.downloads) || 0,
            mainGenre: subjectsArr[0] || 'Geral'
          };
        }).filter(b => b !== null);

        // 2. Filter by Language and Reading Level
        const filtered = normalized.filter(b => {
          const langMatch = b.language === cleanLang;
          const cefrLevel = libraryService.mapReadingLevelToCefr(b.reading_level);
          const levelMatch = cefrLevel.toUpperCase().includes(levelUpper);
          return langMatch && levelMatch;
        });

        // 3. Sort by popularity (downloads DESC)
        filtered.sort((a, b) => (b.downloads || 0) - (a.downloads || 0));

        // 4. Select 5 books from 5 DIFFERENT genres (subjects)
        const selected: RecommendedBook[] = [];
        const seenGenres = new Set<string>();

        for (const book of filtered) {
          const filteredTags = libraryService.filterTags(book.subjects);
          const genreKey = (filteredTags[0] || book.mainGenre || 'Geral').toLowerCase();

          if (!seenGenres.has(genreKey)) {
            seenGenres.add(genreKey);
            selected.push({ ...book, mainGenre: filteredTags[0] || book.mainGenre });
          }
          if (selected.length >= 5) break;
        }

        // Fill remaining slots if fewer than 5 unique genres exist
        if (selected.length < 5) {
          for (const book of filtered) {
            if (!selected.some(b => b.id === book.id)) {
              const filteredTags = libraryService.filterTags(book.subjects);
              selected.push({ ...book, mainGenre: filteredTags[0] || book.mainGenre });
            }
            if (selected.length >= 5) break;
          }
        }

        if (isMounted) {
          setRecommendedBooks(selected);
          setLoading(false);
        }
      } catch (err) {
        console.error("[LevelCompletionModal] Erro ao carregar recomendações:", err);
        if (isMounted) setLoading(false);
      }
    };

    loadRecommendations();

    return () => {
      isMounted = false;
    };
  }, [isOpen, cleanLang, levelUpper]);

  // Handle Save Book to My Shelf
  const handleSaveBookToShelf = async (book: RecommendedBook) => {
    if (!book || savingBookId) return;
    setSavingBookId(book.id);

    try {
      // 1. Save locally via storageService
      storageService.saveBook({
        id: book.id,
        title: book.title,
        author: book.author,
        coverUrl: book.cover || `generated://${book.id}`,
        fileUrl: book.file,
        lastPage: 1,
        totalPages: 0,
        dateAdded: Date.now(),
        category: `LibriTui Library (HTML)`,
        isFavorite: false,
        language: book.language,
        format: 'html',
        description: book.description,
        reading_level: book.reading_level,
        subjects: book.subjects
      });

      // 2. Sync to Cloudflare D1 books table & user_progress table with source: 'roadmap'
      if (user?.id) {
        await dbManager.from('books', user.id).upsert({
          id: book.id,
          user_id: user.id,
          title: book.title,
          author: book.author,
          cover_url: book.cover || `generated://${book.id}`,
          file_url: book.file,
          file_type: 'html',
          language: book.language,
          reading_level: String(book.reading_level || ''),
          description: book.description,
          updated_at: new Date().toISOString()
        }).catch(() => {});

        await dbManager.from('user_progress', user.id).upsert({
          user_id: user.id,
          book_id: book.id,
          current_page: 1,
          percentage: 0,
          last_read: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          source: 'roadmap'
        }).catch(() => {});
      }

      storageService.saveBookProgress(user?.id, book.id, 1, 0, 'roadmap');

      setIsBookSaved(true);
    } catch (err) {
      console.error("[LevelCompletionModal] Erro ao salvar livro:", err);
    } finally {
      setSavingBookId(null);
    }
  };

  const handleGoToShelf = () => {
    onClose();
    navigate('/library?filter=adicionados_recentemente');
  };

  if (!isOpen) return null;

  const I18N_TEXTS: Record<string, any> = {
    pt: {
      congratsTitle: `PARABÉNS! VOCÊ CONCLUIU O NÍVEL ${levelUpper}! 🏆`,
      subtitle: `Separamos 5 livros populares de nível ${levelUpper} em ${targetLangName} recomendados para você praticar sua leitura:`,
      savedNotification: 'Parabéns! Seu livro foi salvo. Lembre-se de acessar a seção "Minha Estante" para iniciar sua leitura.',
      continueStudies: 'Continuar estudos no Roadmap',
      goToShelf: 'Ir para Minha Estante',
      saveToShelf: 'Salvar na Estante',
      viewDetails: 'Ver Detalhes',
      backToList: 'Voltar para recomendações'
    },
    en: {
      congratsTitle: `CONGRATULATIONS! YOU COMPLETED LEVEL ${levelUpper}! 🏆`,
      subtitle: `We selected 5 popular ${levelUpper} level books in ${targetLangName} recommended for your reading practice:`,
      savedNotification: 'Congratulations! Your book was saved. Remember to visit "My Shelf" to start reading.',
      continueStudies: 'Continue studies in Roadmap',
      goToShelf: 'Go to My Shelf',
      saveToShelf: 'Save to My Shelf',
      viewDetails: 'View Details',
      backToList: 'Back to recommendations'
    },
    es: {
      congratsTitle: `¡FELICITACIONES! ¡COMPLETASTE EL NIVEL ${levelUpper}! 🏆`,
      subtitle: `Seleccionamos 5 libros populares de nivel ${levelUpper} en ${targetLangName} recomendados para practicar tu lectura:`,
      savedNotification: '¡Felicitaciones! Tu libro fue guardado. Recuerda visitar "Mi Estantería" para comenzar a leer.',
      continueStudies: 'Continuar estudios en el Roadmap',
      goToShelf: 'Ir a Mi Estantería',
      saveToShelf: 'Guardar en Mi Estantería',
      viewDetails: 'Ver Detalles',
      backToList: 'Volver a recomendaciones'
    },
    fr: {
      congratsTitle: `FÉLICITATIONS ! VOUS AVEZ TERMINÉ LE NIVEAU ${levelUpper} ! 🏆`,
      subtitle: `Nous avons sélectionné 5 livres populaires de niveau ${levelUpper} en ${targetLangName} recommandés pour votre lecture :`,
      savedNotification: 'Félicitations ! Votre livre a été enregistré. N\'oubliez pas de consulter "Mon Étagère" pour commencer à lire.',
      continueStudies: 'Continuer les études dans le Roadmap',
      goToShelf: 'Aller à Mon Étagère',
      saveToShelf: 'Enregistrer sur Mon Étagère',
      viewDetails: 'Voir les Détails',
      backToList: 'Retour aux recommandations'
    },
    de: {
      congratsTitle: `HERZLICHEN GLÜCKWUNSCH! DU HAST LEVEL ${levelUpper} ABGESCHLOSSEN! 🏆`,
      subtitle: `Wir haben 5 beliebte Bücher der Stufe ${levelUpper} auf ${targetLangName} für deine Lesepraxis ausgewählt:`,
      savedNotification: 'Glückwunsch! Dein Buch wurde gespeichert. Denke daran, "Mein Regal" zu besuchen, um mit dem Lesen zu beginnen.',
      continueStudies: 'Studium im Roadmap fortsetzen',
      goToShelf: 'Zu Meinem Regal',
      saveToShelf: 'Ins Regal Speichern',
      viewDetails: 'Details Anzeigen',
      backToList: 'Zurück zu den Empfehlungen'
    },
    it: {
      congratsTitle: `CONGRATULAZIONI! HAI COMPLETATO IL LIVELLO ${levelUpper}! 🏆`,
      subtitle: `Abbiamo selezionato 5 libri popolari di livello ${levelUpper} in ${targetLangName} consigliati per la tua lettura:`,
      savedNotification: 'Congratulazioni! Il tuo libro è stato salvato. Ricordati di visitare "La Mia Libreria" per iniziare a leggere.',
      continueStudies: 'Continua gli studi nel Roadmap',
      goToShelf: 'Vai alla Mia Libreria',
      saveToShelf: 'Salva nella Mia Libreria',
      viewDetails: 'Vedi Dettagli',
      backToList: 'Torna ai consigli'
    },
    hu: {
      congratsTitle: `GRATULÁLUNK! TELJESÍTETTED A(Z) ${levelUpper} SZINTET! 🏆`,
      subtitle: `Kiválasztottunk 5 népszerű ${levelUpper} szintű könyvet ${targetLangName} nyelven az olvasási gyakorláshoz:`,
      savedNotification: 'Gratulálunk! A könyved elmentve. Ne felejtsd el meglátogatni a "Saját Polcom" részt az olvasás megkezdéséhez.',
      continueStudies: 'Tanulás folytatása a Roadmap-ben',
      goToShelf: 'Ugrás a Saját Polcra',
      saveToShelf: 'Mentés a Saját Polcra',
      viewDetails: 'Részletek Megtekintése',
      backToList: 'Vissza az ajánlásokhoz'
    },
    ru: {
      congratsTitle: `ПОЗДРАВЛЯЕМ! ВЫ ЗАВЕРШИЛИ УРОВЕНЬ ${levelUpper}! 🏆`,
      subtitle: `Мы выбрали 5 популярных книг уровня ${levelUpper} на ${targetLangName} языке для вашей практики чтения:`,
      savedNotification: 'Поздравляем! Ваша книга сохранена. Не забудьте посетить "Мою Полку", чтобы начать чтение.',
      continueStudies: 'Продолжить обучение в Roadmap',
      goToShelf: 'Перейти в Мою Полку',
      saveToShelf: 'Сохранить на Полку',
      viewDetails: 'Посмотреть Детали',
      backToList: 'Назад к рекомендациям'
    },
    zh: {
      congratsTitle: `恭喜！您已完成 ${levelUpper} 级别！ 🏆`,
      subtitle: `我们挑选了 5 本热门 ${levelUpper} 级别的 ${targetLangName} 图书推荐供您阅读练习：`,
      savedNotification: '恭喜！您的图书已保存。请记得访问“我的书架”以开始阅读。',
      continueStudies: '在路线图中继续学习',
      goToShelf: '前往我的书架',
      saveToShelf: '保存到书架',
      viewDetails: '查看详情',
      backToList: '返回推荐列表'
    },
    ja: {
      congratsTitle: `おめでとうございます！レベル ${levelUpper} を完了しました！ 🏆`,
      subtitle: `読書練習用に ${targetLangName} のレベル ${levelUpper} の人気書籍 5 冊を厳選しました：`,
      savedNotification: 'おめでとうございます！本が保存されました。「マイ本棚」にアクセスして読書を開始してください。',
      continueStudies: 'ロードマップで学習を続ける',
      goToShelf: 'マイ本棚へ移動',
      saveToShelf: '本棚に保存',
      viewDetails: '詳細を見る',
      backToList: 'おすすめに戻る'
    },
    nl: {
      congratsTitle: `GEFELICITEERD! JE HEBT NIVEAU ${levelUpper} VOLTOOID! 🏆`,
      subtitle: `We hebben 5 populaire boeken van niveau ${levelUpper} in het ${targetLangName} geselecteerd voor je leespratijk:`,
      savedNotification: 'Gefeliciteerd! Je boek is opgeslagen. Vergeet niet "Mijn Plank" te bezoeken om te beginnen met lezen.',
      continueStudies: 'Studie voortzetten in Roadmap',
      goToShelf: 'Ga naar Mijn Plank',
      saveToShelf: 'Opslaan op Plank',
      viewDetails: 'Details bekijken',
      backToList: 'Terug naar aanbevelingen'
    }
  };

  const txt = I18N_TEXTS[cleanNative] || I18N_TEXTS['pt'];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/30 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        className="bg-surface rounded-[28px] w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-black/[0.06] dark:border-white/[0.06]"
      >
        {/* Header */}
        <div className="p-6 border-b border-black/[0.06] bg-gradient-to-r from-warn/10 via-ok/10 to-transparent flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-warn text-surface flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink leading-tight">
                {txt.congratsTitle}
              </h3>
              <p className="text-xs font-semibold text-muted mt-0.5">
                {targetLangName} • Nível {levelUpper}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2.5 text-muted hover:text-muted bg-tint-stone/60 rounded-2xl cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 p-6 overflow-y-auto space-y-6">
          {/* VIEW A: SUCCESS NOTIFICATION VIEW */}
          {isBookSaved ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-6 animate-in fade-in zoom-in-95">
              <div className="w-20 h-20 rounded-full bg-tint-sage dark:bg-tint-sage/60 text-ok flex items-center justify-center ring-8 ring-tint-sage/30">
                <CheckCircle2 size={44} />
              </div>

              <div className="space-y-2 max-w-md">
                <h4 className="text-xl font-medium text-ink tracking-[-0.02em]">
                  {txt.savedNotification}
                </h4>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md pt-4">
                <button
                  onClick={onContinueStudies}
                  className="flex-1 py-4 px-6 bg-gradient-to-r from-ok to-tintd-sky hover:opacity-95 text-white rounded-2xl font-medium text-[13px] transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {txt.continueStudies}
                  <ArrowRight size={16} />
                </button>
                <button
                  onClick={handleGoToShelf}
                  className="flex-1 py-4 px-6 bg-warn hover:bg-warn text-surface rounded-2xl font-medium text-[13px] transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Bookmark size={16} />
                  {txt.goToShelf}
                </button>
              </div>
            </div>
          ) : selectedBook ? (
            /* VIEW B: SELECTED BOOK PREVIEW VIEW */
            <div className="space-y-6 animate-in fade-in duration-200">
              <button
                onClick={() => setSelectedBook(null)}
                className="inline-flex items-center gap-2 text-xs font-bold text-muted hover:text-ink transition-colors cursor-pointer"
              >
                <ChevronLeft size={16} />
                {txt.backToList}
              </button>

              <div className="flex flex-col sm:flex-row gap-6 items-start">
                {/* Book Cover */}
                <div className="w-36 h-52 shrink-0 rounded-2xl bg-surface overflow-hidden relative border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center">
                  {selectedBook.cover ? (
                    <img
                      src={selectedBook.cover}
                      alt={selectedBook.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <BookOpen size={40} className="text-muted" />
                  )}
                  <div className="absolute top-2 left-2 bg-warn text-surface text-[9px] font-medium px-2 py-0.5 rounded-full">
                    {selectedBook.mainGenre}
                  </div>
                </div>

                {/* Details */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-tint-sage dark:bg-tint-sage text-ok dark:text-ok text-[10px] font-medium">
                      Nível {levelUpper}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full bg-tint-stone/60 text-muted text-[10px] font-medium">
                      {targetLangName}
                    </span>
                  </div>

                  <h4 className="text-xl font-medium text-ink tracking-[-0.02em] leading-tight">
                    {selectedBook.title}
                  </h4>
                  <p className="text-xs font-bold text-muted">
                    {selectedBook.author}
                  </p>

                  <p className="text-xs font-medium text-muted leading-relaxed line-clamp-4 pt-1">
                    {selectedBook.description || "Sem descrição disponível."}
                  </p>

                  {/* Save to Shelf Action */}
                  <div className="pt-4">
                    <button
                      onClick={() => handleSaveBookToShelf(selectedBook)}
                      disabled={savingBookId === selectedBook.id}
                      className="text-[13px] font-medium w-full h-12 bg-gradient-to-r from-warn via-warn to-warn hover:opacity-95 text-white rounded-full transition-all hover:scale-[1.01] active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {savingBookId === selectedBook.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Bookmark size={18} />
                      )}
                      {txt.saveToShelf}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* VIEW C: 5 RECOMMENDED BOOKS LIST VIEW */
            <div className="space-y-5">
              <p className="text-xs sm:text-sm font-semibold text-muted leading-relaxed">
                {txt.subtitle}
              </p>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted">
                  <Loader2 className="w-8 h-8 animate-spin text-warn" />
                  <span className="text-xs font-semibold">Carregando livros populares...</span>
                </div>
              ) : recommendedBooks.length === 0 ? (
                <div className="py-8 text-center text-muted text-xs font-medium">
                  Nenhum livro encontrado para este nível no momento.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recommendedBooks.map((book) => (
                    <div
                      key={book.id}
                      onClick={() => setSelectedBook(book)}
                      className="p-4 rounded-2xl bg-tint-stone/40 border border-black/[0.06] hover:border-warn dark:hover:border-warn transition-all flex gap-3.5 items-center cursor-pointer group shadow-xs hover:shadow-md"
                    >
                      {/* Thumbnail */}
                      <div className="w-14 h-20 shrink-0 rounded-xl bg-tint-stone overflow-hidden flex items-center justify-center relative">
                        {book.cover ? (
                          <img src={book.cover} alt={book.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : (
                          <BookOpen className="w-6 h-6 text-muted" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 space-y-1">
                        <span className="inline-block px-2 py-0.5 rounded-md bg-tint-butter dark:bg-tint-butter text-warn dark:text-warn text-[9px] font-medium">
                          {book.mainGenre}
                        </span>
                        <h5 className="text-xs font-semibold text-ink truncate group-hover:text-warn transition-colors">
                          {book.title}
                        </h5>
                        <p className="text-[11px] font-semibold text-muted truncate">
                          {book.author}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Controls (when in list view) */}
        {!isBookSaved && !selectedBook && (
          <div className="p-4 border-t border-black/[0.06] dark:border-white/[0.06] bg-tint-stone/50 flex justify-end">
            <button
              onClick={onContinueStudies}
              className="py-3 px-6 bg-tint-stone text-ink hover:bg-tint-stone rounded-xl font-medium text-[13px] transition-all cursor-pointer"
            >
              {txt.continueStudies}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default LevelCompletionModal;
